import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, hasRole } from "@/lib/auth";
import {
  LayoutDashboard,
  User as UserIcon,
  Target,
  Users as UsersIcon,
  ClipboardCheck,
  Sparkles,
  Settings as SettingsIcon,
  Search,
  Bell,
  LogOut,
  Network,
  Shield,
  ChevronDown,
  ChevronRight,
  Building2,
  Calendar,
  Layers,
  Activity,
  Trophy,
  BookOpen,
  CalendarClock,
  Grid3x3,
  Crown,
  ChartBar as BarChartIcon,
  CreditCard,
  KeyRound,
  LifeBuoy,
  Inbox,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RoleBadge from "@/components/RoleBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useEffect } from "react";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

/**
 * Sidebar information architecture.
 *
 * - `Pinned` items render before any section header (no collapse).
 * - Each `section` is collapsible with state persisted in localStorage
 *   under the key `axia.sidebar.<section.id>.open`.
 * - `defaultOpen: true` only on `me`. The active route auto-expands its
 *   parent section regardless of stored preference.
 * - `roles: null` means "everyone"; otherwise the section is hidden
 *   unless the user matches at least one role.
 */
const PINNED = [
  { to: "/", label: "Home", icon: LayoutDashboard, roles: null },
];

const SECTIONS = [
  {
    id: "me",
    label: "Me",
    defaultOpen: true,
    roles: null,
    items: [
      { to: "/me", label: "Profile", icon: UserIcon, roles: null },
      { to: "/goals", label: "Goals", icon: Target, roles: null },
      { to: "/wins", label: "Wins", icon: Trophy, roles: null },
      { to: "/check-ins", label: "Check-ins", icon: Activity, roles: null },
      { to: "/one-on-ones", label: "1:1s", icon: CalendarClock, roles: null },
      { to: "/signals", label: "Signals", icon: Inbox, roles: null },
      { to: "/reviews", label: "Reviews", icon: ClipboardCheck, roles: null },
    ],
  },
  {
    id: "team",
    label: "My Team",
    defaultOpen: false,
    roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp", "manager"],
    items: [
      { to: "/team", label: "Team Overview", icon: UsersIcon, roles: null },
      { to: "/coaching", label: "Coaching Log", icon: BookOpen, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp", "manager"] },
    ],
  },
  {
    id: "people-ops",
    label: "People Ops",
    defaultOpen: false,
    roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp", "manager", "employee"],
    items: [
      { to: "/org", label: "Org Chart", icon: Network, roles: null },
      { to: "/cycles", label: "Cycles", icon: Calendar, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp"] },
      { to: "/frameworks", label: "Frameworks", icon: Layers, roles: ["super_admin", "tenant_admin", "hr_admin"] },
      { to: "/calibration", label: "Calibration", icon: Grid3x3, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp"] },
      { to: "/succession", label: "Succession", icon: Crown, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp"] },
      { to: "/analytics", label: "Analytics", icon: BarChartIcon, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp"] },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    defaultOpen: false,
    roles: ["super_admin", "tenant_admin", "hr_admin"],
    items: [
      { to: "/admin/users", label: "Users & Roles", icon: Shield, roles: ["super_admin", "tenant_admin", "hr_admin"] },
      { to: "/admin/settings", label: "Workspace settings", icon: SettingsIcon, roles: ["super_admin", "tenant_admin"] },
      { to: "/admin/billing", label: "Billing", icon: CreditCard, roles: ["super_admin", "tenant_admin"] },
      { to: "/admin/platform", label: "Platform", icon: KeyRound, roles: ["super_admin", "tenant_admin", "hr_admin"] },
    ],
  },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/tenant").then((r) => mounted && setTenant(r.data)).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const roleLabel = user?.roles?.includes("super_admin")
    ? "Super Admin"
    : user?.roles?.includes("tenant_admin")
    ? "Tenant Admin"
    : user?.roles?.includes("hr_admin")
    ? "HR Admin"
    : user?.roles?.includes("hrbp")
    ? "HR Business Partner"
    : user?.roles?.includes("manager")
    ? "Manager"
    : "Employee";

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar tenant={tenant} user={user} />
      <div className="md:pl-64 flex flex-col min-h-screen">
        <TopBar user={user} roleLabel={roleLabel} onLogout={logout} tenant={tenant} />
        <main className="flex-1 p-6 lg:p-8" data-testid="app-main">{children}</main>
      </div>
    </div>
  );
}

function Sidebar({ tenant, user }) {
  const location = useLocation();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <aside
      className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:w-64 flex-col bg-white border-r border-slate-200 z-40"
      data-testid="sidebar"
    >
      <div className="px-6 pt-6 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-display text-sm font-bold tracking-tight">
            A
          </div>
          <div>
            <div className="font-display text-lg leading-none font-bold text-slate-900">Axia</div>
            <div className="axia-overline mt-1">Performance, aligned.</div>
          </div>
        </div>
        {tenant && (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <Building2 className="w-3.5 h-3.5" />
            <span className="truncate" data-testid="sidebar-tenant-name">{tenant.name}</span>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {PINNED.filter((i) => !i.roles || hasRole(user, ...i.roles)).map((item) => (
          <NavLink key={item.to} item={item} active={isActive(item.to)} />
        ))}
        {SECTIONS.map((section) => {
          if (section.roles && !hasRole(user, ...section.roles)) return null;
          const items = section.items.filter((i) => !i.roles || hasRole(user, ...i.roles));
          if (items.length === 0) return null;
          const sectionActive = items.some((i) => isActive(i.to));
          return (
            <NavSection
              key={section.id}
              id={section.id}
              label={section.label}
              defaultOpen={section.defaultOpen}
              forceOpen={sectionActive}
            >
              {items.map((item) => (
                <NavLink key={item.to} item={item} active={isActive(item.to)} />
              ))}
            </NavSection>
          );
        })}
      </nav>
      <div className="px-3 py-3 border-t border-slate-100 space-y-2">
        <ExploreAxiaButton />
        <div className="text-[11px] text-slate-400 px-1">
          <span className="inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3" />
            We never store compensation data.
          </span>
        </div>
      </div>
    </aside>
  );
}

function ExploreAxiaButton() {
  const [loading, setLoading] = React.useState(false);
  const [meta, setMeta] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get("/explore/feature-guide/meta");
        if (!cancelled) setMeta(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const download = async () => {
    setLoading(true);
    try {
      const { api } = await import("@/lib/api");
      const res = await api.get("/explore/feature-guide.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "axia-feature-guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Refresh meta — first download primes the cache
      try {
        const m = await api.get("/explore/feature-guide/meta");
        setMeta(m.data);
      } catch { /* silent */ }
    } catch {
      const { toast } = await import("sonner");
      toast.error("Couldn't generate the guide. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <button
        onClick={download}
        disabled={loading}
        data-testid="explore-axia-btn"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition disabled:opacity-60"
      >
        <BookOpen className="w-3.5 h-3.5 text-slate-500" />
        {loading ? "Generating…" : "Explore Axia"}
        <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">PDF</span>
      </button>
      <div
        className="text-[10px] text-slate-400 px-1 leading-snug"
        data-testid="explore-axia-meta"
        title={meta?.last_rendered_at ? `Generated ${new Date(meta.last_rendered_at).toLocaleString()}` : "Not yet generated this session"}
      >
        {meta ? (
          <>
            v{meta.app_version} ·{" "}
            {meta.last_rendered_at
              ? `updated ${relTime(meta.last_rendered_at)}`
              : "ready to generate"}
          </>
        ) : (
          <>&nbsp;</>
        )}
      </div>
    </div>
  );
}

function relTime(iso) {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const s = Math.max(0, Math.round(diffMs / 1000));
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} hr ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d} d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

function NavSection({ id, label, defaultOpen, forceOpen, children }) {
  const storageKey = `axia.sidebar.${id}.open`;
  const readStored = () => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch { /* localStorage unavailable */ }
    return defaultOpen;
  };
  const [open, setOpen] = React.useState(readStored);

  // If the active route lives inside this section, keep it expanded — but
  // do NOT overwrite the user's saved preference; only force visibility.
  const effectiveOpen = open || forceOpen;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="mt-4 first:mt-0">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 hover:text-slate-900 transition rounded"
        data-testid={`nav-section-${id}-toggle`}
        aria-expanded={effectiveOpen}
      >
        <span>{label}</span>
        {effectiveOpen
          ? <ChevronDown className="w-3 h-3 opacity-70" />
          : <ChevronRight className="w-3 h-3 opacity-70" />}
      </button>
      {effectiveOpen && (
        <div className="mt-0.5 space-y-0.5" data-testid={`nav-section-${id}-items`}>
          {children}
        </div>
      )}
    </div>
  );
}

function NavLink({ item, active }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={cn(
        "group flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all",
        active
          ? "bg-slate-100 text-slate-900"
          : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
      )}
    >
      <Icon className={cn("w-4 h-4", active ? "text-slate-900" : "text-slate-400 group-hover:text-slate-700")} />
      <span>{item.label}</span>
      {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-slate-900" />}
    </Link>
  );
}

function TenantSwitcherMenuItem({ currentTenantId }) {
  const [tenants, setTenants] = React.useState(null);
  const [switching, setSwitching] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    api.get("/tenants")
      .then((r) => { if (!cancelled) setTenants(r.data.items || []); })
      .catch(() => { if (!cancelled) setTenants([]); });
    return () => { cancelled = true; };
  }, []);

  const switchTo = async (t) => {
    if (t.id === currentTenantId) return;
    setSwitching(true);
    try {
      const r = await api.post("/auth/switch-tenant", { tenant_id: t.id });
      localStorage.setItem("axia.access", r.data.access_token);
      localStorage.setItem("axia.refresh", r.data.refresh_token || "");
      localStorage.setItem("axia.user", JSON.stringify(r.data.user || {}));
      window.location.href = "/";
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Could not switch tenant");
      setSwitching(false);
    }
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="menu-tenant-switcher">
        <Building2 className="w-4 h-4 mr-2" /> Switch workspace
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="w-64">
          {tenants === null && (
            <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>
          )}
          {tenants && tenants.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">No tenants</div>
          )}
          {tenants && tenants.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onSelect={(e) => { e.preventDefault(); switchTo(t); }}
              disabled={switching}
              data-testid={`switch-to-${t.slug}`}
              className="flex items-center gap-2"
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                style={{ background: t.primary_color || "#0F172A" }}
              >
                {(t.name || "?")[0].toUpperCase()}
              </div>
              <span className="flex-1 truncate">{t.name}</span>
              {t.id === currentTenantId && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">current</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

function TopBar({ user, roleLabel, onLogout }) {
  return (
    <header
      className="sticky top-0 z-30 h-16 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex items-center px-4 lg:px-8 gap-4"
      data-testid="topbar"
    >
      <div className="max-w-xl w-full">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search people, departments…"
            className="pl-9 h-10 bg-slate-50 border-slate-200 focus-visible:bg-white"
            data-testid="topbar-search"
          />
          <kbd className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tracking-wider text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 font-mono">
            ⌘K
          </kbd>
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
      <Link to="/help" className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition" title="Help center" data-testid="topbar-help">
        <LifeBuoy className="w-4 h-4" />
      </Link>
      <NotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-md hover:bg-slate-100 transition"
            data-testid="user-menu-trigger"
          >
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
              {(user?.email || "U").slice(0, 2).toUpperCase()}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-xs font-medium text-slate-900 leading-tight">{user?.email}</div>
              <div className="mt-0.5"><RoleBadge role={roleLabel} /></div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel>
            <div className="text-xs font-normal text-slate-500">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/me" data-testid="menu-my-profile">
              <UserIcon className="w-4 h-4 mr-2" /> My Profile
            </Link>
          </DropdownMenuItem>
          {(user?.roles || []).includes("super_admin") && (
            <TenantSwitcherMenuItem currentTenantId={user?.tenant_id} />
          )}
          <DropdownMenuItem asChild>
            <Link to="/help" data-testid="menu-help">
              <LifeBuoy className="w-4 h-4 mr-2" /> Help center
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={async (e) => {
            e.preventDefault();
            try {
              const res = await api.get("/explore/feature-guide.pdf", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url; a.download = "axia-feature-guide.pdf";
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
            } catch {
              const { toast } = await import("sonner");
              toast.error("Couldn't generate the guide.");
            }
          }} data-testid="menu-explore-axia">
            <BookOpen className="w-4 h-4 mr-2" /> Explore Axia <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">PDF</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="signout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );
}
